import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  LocationOn,
  CloudUpload,
  Image,
  Settings,
  Sync,
  CheckCircle,
  Error as ErrorIcon,
  OpenInNew,
} from '@mui/icons-material';
import {
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
} from '@mui/material';
import apiService from '../../services/api';
import { Club, ExternalIntegrationConfig, ExternalIntegrationStatus, ExternalCourtMapping, Court } from '../../types';

export const AdminClubsPage: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClub, setEditingClub] = useState<Club | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    is_active: true,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  
  // External integration state
  const [externalIntegrationDialogOpen, setExternalIntegrationDialogOpen] = useState(false);
  const [selectedClubForIntegration, setSelectedClubForIntegration] = useState<Club | null>(null);
  const [integrationTab, setIntegrationTab] = useState(0);
  const [integrationConfig, setIntegrationConfig] = useState<ExternalIntegrationConfig>({
    integration_type: 'iledeloisirs',
    court_mappings: [],
    sync_interval_minutes: 60,
  });
  const [integrationStatus, setIntegrationStatus] = useState<ExternalIntegrationStatus | null>(null);
  const [courts, setCourts] = useState<Court[]>([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getClubs();
      // Ensure response.data is always an array
      const data: any = response?.data;
      const clubsArray = Array.isArray(data) ? data : (Array.isArray(data?.clubs) ? data.clubs : (Array.isArray(data?.data) ? data.data : []));
      setClubs(clubsArray);
    } catch (error) {
      console.error('Error fetching clubs:', error);
      setClubs([]); // Always set empty array on error
      setSnackbar({ open: true, message: 'Failed to fetch clubs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClub = () => {
    setEditingClub(null);
    setFormData({
      name: '',
      address: '',
      is_active: true,
    });
    setLogoFile(null);
    setLogoPreview(null);
    setOpenDialog(true);
  };

  const handleEditClub = (club: Club) => {
    setEditingClub(club);
    setFormData({
      name: club.name,
      address: club.address,
      is_active: club.is_active,
    });
    setLogoFile(null);
    // Show existing logo if available
    setLogoPreview((club as any).logo_url || null);
    setOpenDialog(true);
  };

  const handleSaveClub = async () => {
    try {
      if (editingClub) {
        await apiService.updateClub(editingClub.id, formData);
        if (logoFile) {
          await apiService.uploadClubLogo(editingClub.id, logoFile);
        }
        setSnackbar({ open: true, message: 'Club updated successfully', severity: 'success' });
      } else {
        const response = await apiService.createClub(formData);
        if (logoFile && response.data.id) {
          await apiService.uploadClubLogo(response.data.id, logoFile);
        }
        setSnackbar({ open: true, message: 'Club created successfully', severity: 'success' });
      }
      setOpenDialog(false);
      fetchClubs();
    } catch (error) {
      console.error('Error saving club:', error);
      setSnackbar({ open: true, message: 'Failed to save club', severity: 'error' });
    }
  };

  const handleDeleteClub = async (clubId: number, cascade = false) => {
    const msg = cascade ? 'This will also delete all courts and their matches. Continue?' : 'Are you sure you want to delete this club?';
    if (!window.confirm(msg)) return;
    try {
      await apiService.deleteClub(clubId, cascade);
      setSnackbar({ open: true, message: 'Club deleted successfully', severity: 'success' });
      fetchClubs();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || '';
      if (!cascade && typeof detail === 'string' && detail.includes('court(s)')) {
        if (window.confirm('This club has courts. Delete the club and all its courts (and matches)?')) {
          handleDeleteClub(clubId, true);
        }
      } else {
        setSnackbar({ open: true, message: detail || 'Failed to delete club', severity: 'error' });
      }
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenExternalIntegration = async (club: Club) => {
    setSelectedClubForIntegration(club);
    setExternalIntegrationDialogOpen(true);
    setIntegrationTab(0);

    try {
      const courtsResponse = await apiService.getCourts();
      const allCourts = Array.isArray(courtsResponse.data) ? courtsResponse.data : [];
      const clubCourts = allCourts.filter((c: Court) => c.club_id === club.id);
      setCourts(clubCourts);

      const integrationType = (club as { external_integration_type?: string }).external_integration_type || 'iledeloisirs';
      const storedMappings = (club as { external_court_mappings?: Record<string, Record<string, unknown>> }).external_court_mappings;
      const syncInterval = (club as { external_sync_interval_minutes?: number }).external_sync_interval_minutes ?? 60;

      let mappings: ExternalCourtMapping[];
      if (storedMappings && typeof storedMappings === 'object' && Object.keys(storedMappings).length > 0) {
        mappings = Object.entries(storedMappings).map(([cid, config]) => {
          const cfg = config as Record<string, unknown>;
          const courtId = parseInt(cid, 10);
          const court = clubCourts.find((c: Court) => c.id === courtId);
          const mapType = (cfg.integration_type as string) || integrationType;
          return {
            court_id: courtId,
            integration_type: mapType,
            name: (cfg.name as string) ?? court?.name ?? `Court ${cid}`,
            staff_id: (cfg.staff_id as number) ?? 0,
            product_id: (cfg.product_id as number) ?? 23706,
            product_url: (cfg.product_url as string) ?? 'https://cergy-pontoise.iledeloisirs.fr/boutique/produit/padel/',
            city: (cfg.city as string) ?? '',
            club_id: (cfg.club_id as string) ?? '',
            service_id: (cfg.service_id as string) ?? '',
            ballejaune_widget_id: (cfg.ballejaune_widget_id as number) ?? undefined,
            ballejaune_key: (cfg.ballejaune_key as string) ?? '',
            schedule_id: (cfg.schedule_id as number) ?? undefined,
          };
        });
      } else {
        if (integrationType === 'anybuddy') {
          mappings = [{ court_id: null, integration_type: 'anybuddy', name: '', city: '', club_id: '', service_id: '' }];
        } else if (integrationType === 'ballejaune') {
          mappings = [{ court_id: null, integration_type: 'ballejaune', name: '', ballejaune_widget_id: 195, ballejaune_key: '', schedule_id: undefined }];
        } else {
          mappings = [{ court_id: null, integration_type: 'iledeloisirs', name: '', staff_id: 0, product_id: 23706, product_url: 'https://cergy-pontoise.iledeloisirs.fr/boutique/produit/padel/' }];
        }
      }

      setIntegrationConfig({
        integration_type: integrationType,
        court_mappings: mappings,
        sync_interval_minutes: syncInterval,
      });

      try {
        const statusResponse = await apiService.getExternalIntegrationStatus(club.id);
        setIntegrationStatus(statusResponse.data);
      } catch (error) {
        console.error('Error loading integration status:', error);
      }
    } catch (error) {
      console.error('Error loading courts:', error);
      setSnackbar({ open: true, message: 'Failed to load courts', severity: 'error' });
    }
  };

  const handleSaveExternalIntegration = async () => {
    if (!selectedClubForIntegration) return;

    try {
      const response = selectedClubForIntegration.external_sync_enabled
        ? await apiService.updateExternalIntegration(selectedClubForIntegration.id, integrationConfig)
        : await apiService.enableExternalIntegration(selectedClubForIntegration.id, integrationConfig);
      setSnackbar({ open: true, message: 'External integration saved successfully', severity: 'success' });
      fetchClubs();
      if (response?.data) setSelectedClubForIntegration(response.data as Club);
      const statusResponse = await apiService.getExternalIntegrationStatus(selectedClubForIntegration.id);
      setIntegrationStatus(statusResponse.data);
    } catch (error: any) {
      console.error('Error saving external integration:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to save external integration',
        severity: 'error',
      });
    }
  };

  const handleDisableExternalIntegration = async () => {
    if (!selectedClubForIntegration) return;
    
    if (window.confirm('Are you sure you want to disable external integration for this club?')) {
      try {
        await apiService.disableExternalIntegration(selectedClubForIntegration.id);
        setSnackbar({ open: true, message: 'External integration disabled', severity: 'success' });
        fetchClubs();
        setExternalIntegrationDialogOpen(false);
      } catch (error) {
        console.error('Error disabling external integration:', error);
        setSnackbar({ open: true, message: 'Failed to disable external integration', severity: 'error' });
      }
    }
  };

  const handleSyncNow = async () => {
    if (!selectedClubForIntegration) return;
    
    setSyncing(true);
    try {
      await apiService.syncExternalIntegration(selectedClubForIntegration.id);
      setSnackbar({ open: true, message: 'Sync started successfully', severity: 'success' });
      // Reload status after a short delay
      setTimeout(async () => {
        try {
          const statusResponse = await apiService.getExternalIntegrationStatus(selectedClubForIntegration!.id);
          setIntegrationStatus(statusResponse.data);
        } catch (error) {
          console.error('Error reloading status:', error);
        }
      }, 2000);
    } catch (error: any) {
      console.error('Error syncing:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to sync', 
        severity: 'error' 
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateCourtMapping = (index: number, field: keyof ExternalCourtMapping, value: any) => {
    const newMappings = [...integrationConfig.court_mappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setIntegrationConfig({ ...integrationConfig, court_mappings: newMappings });
  };

  const handleAddCourtMapping = (syncType?: 'iledeloisirs' | 'anybuddy' | 'ballejaune') => {
    const type = syncType ?? integrationConfig.integration_type;
    let newRow: ExternalCourtMapping;
    if (type === 'anybuddy') {
      newRow = { court_id: null, integration_type: 'anybuddy', name: '', city: '', club_id: '', service_id: '' };
    } else if (type === 'ballejaune') {
      newRow = { court_id: null, integration_type: 'ballejaune', name: '', ballejaune_widget_id: 195, ballejaune_key: '', schedule_id: undefined };
    } else {
      newRow = { court_id: null, integration_type: 'iledeloisirs', name: '', staff_id: 0, product_id: 23706, product_url: 'https://cergy-pontoise.iledeloisirs.fr/boutique/produit/padel/' };
    }
    setIntegrationConfig({
      ...integrationConfig,
      court_mappings: [...integrationConfig.court_mappings, newRow],
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Club Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateClub}
        >
          Add Club
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{clubs.length}</Typography>
                  <Typography color="textSecondary">Total Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocationOn color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {clubs.length}
                  </Typography>
                  <Typography color="textSecondary">Total Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {clubs.filter(club => club.is_active).length}
                  </Typography>
                  <Typography color="textSecondary">Active Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {clubs.filter(club => !club.is_active).length}
                  </Typography>
                  <Typography color="textSecondary">Inactive Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Clubs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>External Integration</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clubs.map((club) => (
                <TableRow key={club.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {(club as any).logo_url ? (
                        <img 
                          src={(club as any).logo_url.startsWith('http') ? (club as any).logo_url : `${window.location.origin}${(club as any).logo_url}`}
                          alt={`${club.name} logo`}
                          style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Image color="disabled" />
                      )}
                      <Typography variant="subtitle2" fontWeight="bold">
                        {club.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <LocationOn fontSize="small" sx={{ mr: 1 }} />
                      {club.address}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={club.is_active ? 'Active' : 'Inactive'}
                      color={club.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={club.external_sync_enabled ? 'Enabled' : 'Disabled'}
                      color={club.external_sync_enabled ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleOpenExternalIntegration(club)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit Club">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditClub(club)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Configure External Integration">
                        <IconButton 
                          size="small" 
                          color="info"
                          onClick={() => handleOpenExternalIntegration(club)}
                        >
                          <Settings />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Club">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteClub(club.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClub ? 'Edit Club' : 'Create New Club'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="Club Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </Box>
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  required
                />
              </Box>
            </Box>
            
            {/* Logo Upload Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Club Logo
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                  >
                    Upload Logo
                  </Button>
                </label>
                {(logoPreview || (editingClub && (editingClub as any).logo_url)) && (
                  <Box>
                    <img 
                      src={logoPreview || (editingClub && (editingClub as any).logo_url)} 
                      alt="Logo preview"
                      style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover' }}
                    />
                  </Box>
                )}
                {logoFile && (
                  <Typography variant="body2" color="text.secondary">
                    {logoFile.name}
                  </Typography>
                )}
                {editingClub && (editingClub as any).logo_url && !logoFile && (
                  <Typography variant="body2" color="text.secondary">
                    Current logo
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveClub} variant="contained">
            {editingClub ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* External Integration Dialog */}
      <Dialog 
        open={externalIntegrationDialogOpen} 
        onClose={() => setExternalIntegrationDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          External Integration - {selectedClubForIntegration?.name}
        </DialogTitle>
        <DialogContent>
          <Tabs value={integrationTab} onChange={(e, newValue) => setIntegrationTab(newValue)}>
            <Tab label="Configuration" />
            <Tab label="Status" />
          </Tabs>
          
          {integrationTab === 0 && (
            <Box mt={3}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Default sync type for new mappings</InputLabel>
                <Select
                  value={integrationConfig.integration_type}
                  label="Default sync type for new mappings"
                  onChange={(e) => setIntegrationConfig({ ...integrationConfig, integration_type: e.target.value })}
                >
                  <MenuItem value="iledeloisirs">Île de Loisirs</MenuItem>
                  <MenuItem value="anybuddy">Anybuddy</MenuItem>
                  <MenuItem value="ballejaune">BalleJaune</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Sync Interval (minutes)"
                type="number"
                value={integrationConfig.sync_interval_minutes}
                onChange={(e) => setIntegrationConfig({ ...integrationConfig, sync_interval_minutes: parseInt(e.target.value) || 60 })}
                sx={{ mb: 3 }}
              />

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Court Mappings
                </Typography>
                <Box display="flex" gap={0.5}>
                  <Button size="small" variant="outlined" onClick={() => handleAddCourtMapping('iledeloisirs')}>
                    + Île de Loisirs
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleAddCourtMapping('anybuddy')}>
                    + Anybuddy
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleAddCourtMapping('ballejaune')}>
                    + BalleJaune
                  </Button>
                </Box>
              </Box>
              <MuiTable size="small">
                <MuiTableHead>
                  <MuiTableRow>
                    <MuiTableCell>Court Name</MuiTableCell>
                    <MuiTableCell>Sync Type</MuiTableCell>
                    <MuiTableCell>City (slug)</MuiTableCell>
                    <MuiTableCell>Club ID</MuiTableCell>
                    <MuiTableCell>Service ID</MuiTableCell>
                    <MuiTableCell>Widget ID</MuiTableCell>
                    <MuiTableCell>Key</MuiTableCell>
                    <MuiTableCell>Schedule ID</MuiTableCell>
                    <MuiTableCell>Staff ID</MuiTableCell>
                    <MuiTableCell>Product ID</MuiTableCell>
                    <MuiTableCell>Product URL</MuiTableCell>
                  </MuiTableRow>
                </MuiTableHead>
                <MuiTableBody>
                  {integrationConfig.court_mappings.map((mapping, index) => {
                    const isAnybuddy = (mapping.integration_type ?? integrationConfig.integration_type) === 'anybuddy';
                    const isBallejaune = (mapping.integration_type ?? integrationConfig.integration_type) === 'ballejaune';
                    const isIleDeLoisirs = !isAnybuddy && !isBallejaune;
                    return (
                    <MuiTableRow key={mapping.court_id ?? `new-${index}`}>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Court name"
                          value={mapping.name ?? ''}
                          onChange={(e) => handleUpdateCourtMapping(index, 'name', e.target.value)}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <Select
                          size="small"
                          fullWidth
                          value={mapping.integration_type ?? integrationConfig.integration_type}
                          onChange={(e) => handleUpdateCourtMapping(index, 'integration_type', e.target.value)}
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value="iledeloisirs">Île de Loisirs</MenuItem>
                          <MenuItem value="anybuddy">Anybuddy</MenuItem>
                          <MenuItem value="ballejaune">BalleJaune</MenuItem>
                        </Select>
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. herblay-95220-fr"
                          value={mapping.city ?? ''}
                          onChange={(e) => handleUpdateCourtMapping(index, 'city', e.target.value)}
                          disabled={!isAnybuddy}
                          sx={!isAnybuddy ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. padel-camp-acheres"
                          value={mapping.club_id ?? ''}
                          onChange={(e) => handleUpdateCourtMapping(index, 'club_id', e.target.value)}
                          disabled={!isAnybuddy}
                          sx={!isAnybuddy ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Optional"
                          value={mapping.service_id ?? ''}
                          onChange={(e) => handleUpdateCourtMapping(index, 'service_id', e.target.value)}
                          disabled={!isAnybuddy}
                          sx={!isAnybuddy ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          placeholder="e.g. 195"
                          value={mapping.ballejaune_widget_id ?? ''}
                          onChange={(e) => handleUpdateCourtMapping(index, 'ballejaune_widget_id', parseInt(e.target.value, 10) || undefined)}
                          disabled={!isBallejaune}
                          sx={!isBallejaune ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="API key"
                          value={mapping.ballejaune_key ?? ''}
                          onChange={(e) => handleUpdateCourtMapping(index, 'ballejaune_key', e.target.value)}
                          disabled={!isBallejaune}
                          sx={!isBallejaune ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          placeholder="e.g. 39969"
                          value={mapping.schedule_id ?? ''}
                          onChange={(e) => handleUpdateCourtMapping(index, 'schedule_id', parseInt(e.target.value, 10) || undefined)}
                          disabled={!isBallejaune}
                          sx={!isBallejaune ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          placeholder="Staff ID"
                          value={mapping.staff_id ?? 0}
                          onChange={(e) => handleUpdateCourtMapping(index, 'staff_id', parseInt(e.target.value, 10) || 0)}
                          disabled={isAnybuddy || isBallejaune}
                          sx={isAnybuddy || isBallejaune ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={mapping.product_id ?? 23706}
                          onChange={(e) => handleUpdateCourtMapping(index, 'product_id', parseInt(e.target.value, 10) || 23706)}
                          disabled={isAnybuddy || isBallejaune}
                          sx={isAnybuddy || isBallejaune ? { opacity: 0.5 } : undefined}
                        />
                      </MuiTableCell>
                      <MuiTableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="https://..."
                            value={mapping.product_url ?? ''}
                            onChange={(e) => handleUpdateCourtMapping(index, 'product_url', e.target.value)}
                            disabled={isAnybuddy || isBallejaune}
                            sx={isAnybuddy || isBallejaune ? { opacity: 0.5 } : undefined}
                          />
                          {!isAnybuddy && !isBallejaune && (mapping.product_url ?? '').trim() && (
                            <Tooltip title="Open booking page">
                              <IconButton
                                size="small"
                                href={(mapping.product_url ?? '').trim()}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open booking page"
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </MuiTableCell>
                    </MuiTableRow>
                  );})}
                </MuiTableBody>
              </MuiTable>
            </Box>
          )}
          
          {integrationTab === 1 && integrationStatus && (
            <Box mt={3}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Chip
                  icon={integrationStatus.enabled ? <CheckCircle /> : <ErrorIcon />}
                  label={integrationStatus.enabled ? 'Enabled' : 'Disabled'}
                  color={integrationStatus.enabled ? 'success' : 'default'}
                />
                {integrationStatus.integration_type && (
                  <Typography variant="body2" color="text.secondary">
                    Type: {integrationStatus.integration_type}
                  </Typography>
                )}
              </Box>
              
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Last Sync:
                </Typography>
                <Typography variant="body1">
                  {integrationStatus.last_sync_at 
                    ? new Date(integrationStatus.last_sync_at).toLocaleString()
                    : 'Never'}
                </Typography>
              </Box>
              
              {integrationStatus.sync_error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {integrationStatus.sync_error}
                </Alert>
              )}
              
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<Sync />}
                  onClick={handleSyncNow}
                  disabled={syncing || !integrationStatus.enabled}
                >
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>
                {integrationStatus.enabled && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDisableExternalIntegration}
                  >
                    Disable Integration
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExternalIntegrationDialogOpen(false)}>Close</Button>
          {integrationTab === 0 && (
            <Button onClick={handleSaveExternalIntegration} variant="contained">
              Save Configuration
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
